import React from 'react';
import { observer } from 'mobx-react';
import { UiStore } from '@gamiphy/loyalty-station-desktop/store/ui';
import { Grid, Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SVG from 'react-inlinesvg';
import { useMobile } from '@gamiphy/loyalty-station-desktop/utils/mobile';
import classNames from 'classnames';
import { ThemeStore } from '@gamiphy/loyalty-station-desktop/store/theme';
import { LauncherPositions } from '@gamiphy/bl/theme';
import giftIcon from '../assets/gift.svg';

// Helper functions for sx
const getRootSx = (theme: Theme, size: number) => ({
  width: size,
  height: size,
  borderRadius: size / 2,
  cursor: 'pointer',
  position: 'relative',
  background: theme.palette.primary.main,
  transition: 'all 200ms linear',
});

const getIconContainerSx = (theme: Theme, size: number, isPositionRight: boolean) => ({
  transform: theme.direction === 'rtl' && isPositionRight ? 'rotate(180deg)' : 'rotate(0)',
  position: 'absolute',
  left: size * 0.3,
  bottom: size * 0.25,
});

const getIconSx = (theme: Theme, size: number) => ({
  fill: theme.palette.text.secondary,
  width: size * 0.4,
  height: size * 0.4,
});

type Props = {
  changeShowTitle: (visible: boolean) => void,
}

const GiftIcon: React.FC<Props> = observer(({ changeShowTitle }) => {
  const theme = useTheme();
  const [expandedMobile, setExpandedMobile] = React.useState(false);
  const isPositionRight = React.useMemo(() => {
    switch (ThemeStore.theme.launcher.position.mobile) {
      case LauncherPositions.right:
      case LauncherPositions.topRight:
      case LauncherPositions.bottomRight:
        return true;
      default:
        return false;
    }
  }, [ThemeStore.theme.launcher.position.mobile]);

  const size = ThemeStore.iconSize;
  const isMobile = useMobile();

  const handleClick = React.useCallback(() => {
    if (isMobile) {
      changeShowTitle(true);
      setExpandedMobile(true);
      setTimeout(() => {
        UiStore.open();
        changeShowTitle(false);
        setExpandedMobile(false);
      }, 400);
    } else {
      UiStore.open();
      changeShowTitle(false);
    }
  }, [isMobile]);

  return (
    <Grid
      onMouseEnter={() => (!isMobile ? changeShowTitle(true) : null)}
      onMouseLeave={() => (!isMobile ? changeShowTitle(false) : null)}
      className={classNames(!isMobile ? 'hoverEffect' : 'clickEffect')}
      onClick={handleClick}
      sx={{
        ...getRootSx(theme, size),
        transform: theme.direction === 'rtl' && isPositionRight ? 'rotate(180deg)' : 'rotate(0)',
        ...isMobile
          ? {
            '&:hover': {
              width: !UiStore.opened && expandedMobile ? '100%' : size,
            },
          }
          : {
            '&:hover': {
              width: !UiStore.opened ? '100%' : size,
            },
          },
      }}
    >
      <Grid sx={getIconContainerSx(theme, size, isPositionRight)}>
        <SVG src={giftIcon} style={getIconSx(theme, size)} />
      </Grid>
    </Grid>
  );
});

export default GiftIcon;
